import { ExpiresAtCountdown, Currency } from 'components'
import messages from 'i18n/messages'
import first from 'lodash/first'
import flowRight from 'lodash/flowRight'
import isEmpty from 'lodash/isEmpty'
import { makeSelectDateByOrderId } from 'logic/dates'
import { makeSelectEventByOrderId } from 'logic/events'
import { isValidEmail, makeSelectCustomer } from 'logic/customers'
import { makeSelectCurOrg } from 'logic/orgs'
import { updateOrderAttr, makeSelectBookingType } from 'logic/orders'
import { calcDeliveryCosts, calcFees, calcSubtotal, calcTotal, makeSelectLoading } from 'logic/payment'
import PropTypes from 'prop-types'
import { Component } from 'react'
import { FormattedDate, FormattedMessage, injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { createStructuredSelector } from 'reselect'
import update from 'immutability-helper'
import { atsxRegex } from 'components/OescvContactForm'

const ATSX_REGEX = 'Please provide a valid ATSX # like \'AUT1234F\''

export const SecSgrInfo = ({ name, num, label }) => (
  <span>
    {isEmpty(name) ? <FormattedMessage {...messages[label]} /> : name}
    {isEmpty(num) ? '' : ` ${num}`}
    {isEmpty(name) && isEmpty(num) ? '' : ` `}
  </span>
)

export const SeatInfo = ({ seat }) => (
  <span className="seat-info">
    <SecSgrInfo name={seat.sec_name} num={seat.sec_num} label="section" />
    <SecSgrInfo name={seat.sgr_name} num={seat.sgr_num} label="seatgroup" />
    <span>{seat.label || <FormattedMessage {...messages.seat} />}</span> {seat.override_num}
  </span>
)

const SummaryRowItem = ({ item }) => (
  <tr>
    <td>{item.amount}</td>
    <td>{item.description}</td>
    <td className="text-right"><Currency value={item.value * item.amount} /></td>
  </tr>
)

class PaymentSummary extends Component {

  state = {
    error: null,
  }

  eventImgSrc = event =>
    event.images && first(event.images) ? first(event.images).thumb : null

  eventBgImgstyles = event => (
    {
      backgroundImage: `url(${this.eventImgSrc(event)}`,
    }
  )

  handleToggleRes = () =>
    this.props.actions.updateOrderAttr('res', !this.props.order.res)

  canContinue = () => {
    if (this.props.status === 'missing_data') return false

    const { customer } = this.props

    if (!this.props.agreedTerms) return false
    if (isEmpty(customer.email)) return false
    if (customer.meta && customer.meta.atsx_num) {
      if (new RegExp(atsxRegex).test(customer && customer.meta.atsx_num)) return true // shortcut to exit for oescv
      if (this.state.error !== ATSX_REGEX)
        this.setState(update(this.state, { error: { $set: ATSX_REGEX } }))
    }
    if (isEmpty(customer.firstname)) return false
    if (isEmpty(customer.lastname)) return false
    if (!isValidEmail(customer.email)) return false
    if (this.props.deliveryMethod === 'post' && customer.addresses.length > 0) {
      if (isEmpty(customer.addresses[0].street)) return false
      if (isEmpty(customer.addresses[0].city)) return false
      if (isEmpty(customer.addresses[0].country_code)) return false
      if (isEmpty(customer.addresses[0].postcode)) return false
    }
    return true
  }

  render() {
    const { order, org, bookingType, values, deliveryMethod, event } = this.props

    const subtotal = calcSubtotal(values, bookingType === 'buy')
    const total = calcTotal(values, bookingType === 'buy')
    const deliveryCosts = calcDeliveryCosts(org, deliveryMethod, first(values).description)
    const fees = calcFees(subtotal, org, values.length)
    // const vat = this.calcVat(total, org, fees)
    return (
      <div className="payment-summary payment-summary-wrap">
        <h2>
          <div className="title"><FormattedMessage {...messages.summary} /></div>
        </h2>
        {order
          ? <div className="date-info" style={{ marginTop: '2em' }}>
            <div className="date-img" style={event ? this.eventBgImgstyles(event) : {}} />
            <div className="date-text">
              <small><FormattedMessage {...messages.event} /></small>
              <div className="date-name">
                {first(order.event_dates_names)}
              </div>
              <div className="date-date">
                <FormattedDate value={first(order.event_dates_dates)} day="numeric" month="short" year="numeric" hour="numeric" minute="numeric" />
              </div>
            </div>
          </div>
          : null
        }
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: '5em' }}><FormattedMessage {...messages.amount} /></th>
              <th><FormattedMessage {...messages.description} /></th>
              <th className="text-right"><FormattedMessage {...messages.totalPrice} /> [{org.currency}]</th>
            </tr>
          </thead>
          <tbody>
            {values.map((item, index) =>
              item.description
                ? <SummaryRowItem key={`item_${index}`} item={item} />
                : <SummarySeatRowItem key={`item_${index}`} seat={item} bookingType={bookingType} />
            )}
          </tbody>
          <tfoot>
            {process.env.HZL_INST || this.props.skipFees
              ? null
              : <tr style={{ color: '#a03' }}>
                  <td></td>
                  <td>
                    <FormattedMessage {...messages.fees} /><br />
                    <small><b><FormattedMessage {...messages.feesPaidByTicketteer} /></b></small>
                  </td>
                  <td className="text-right" style={{ textDecoration: 'line-through' }}><Currency value={total * 0.084} /></td>
                </tr>
            }
            {bookingType === 'buy' && !this.props.skipTotal
              ? <tr className="total">
                <td>{values.length}</td>
                <td>
                  <FormattedMessage {...messages.total} />
                </td>
                <td className="text-right"><Currency value={(total + deliveryCosts + fees)} /></td>
              </tr>
              : null
            }
          </tfoot>
        </table>
      </div>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  org: makeSelectCurOrg(),
  bookingType: makeSelectBookingType(),
  loading: makeSelectLoading(),
  date: makeSelectDateByOrderId(props => props.order ? first(props.order.event_date_ids) : null),
  event: makeSelectEventByOrderId(props => props.order ? first(props.order.event_date_ids) : null),
  customer: makeSelectCustomer(),
})

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    updateOrderAttr,
  }, dispatch)
})

const enhance = flowRight(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps)
)

PaymentSummary.propTypes = {
  values: PropTypes.array.isRequired,
  status: PropTypes.string,
  skipFees: PropTypes.bool,
  skipTotal: PropTypes.bool,
  deliveryMethod: PropTypes.string,
}

export default enhance(PaymentSummary)
